import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiClient } from '../client';
import { COLLECTION_STATUSES } from '@/types/collection';
import {
  PACKAGE_STATUSES,
  type CustomerShipmentDetail,
  type CustomerShipmentRow,
  type GroundHandlerShipmentDetail,
  type GroundHandlerShipmentRow,
  type NoaPart,
  type ShipmentDetail,
  type ShipmentPackageRow,
  type ShipmentRow,
  type ShipmentStatus,
  type SubscriptionOption,
} from '@/types/shipment';

/**
 * 'handler' and 'customer' are discriminants for `ShipmentListScreen`/
 * `ShipmentDetailScreen`'s `scope` prop only — each is a structurally different
 * row/detail shape from the Tollit one (see `GroundHandlerShipmentRow` and
 * `CustomerShipmentRow`: neither carries `subscriptionId`, and only the ground
 * handler keeps `customerName`), so each gets its own sibling functions below
 * instead of a shared `BASE`/`shipmentsQueryOptions`.
 */
export type ShipmentScope = 'tollit' | 'handler' | 'customer';

const BASE = '/api/v1/shipments';

const shipmentRowSchema = z.object({
  customerName: z.string().nullable(),
  subscriptionId: z.string(),
  transportDocumentNo: z.string(),
  manifestNo: z.string().nullable(),
  flightNo: z.string(),
  eta: z.string(),
  noaDateTime: z.string().nullable(),
  noaQuantity: z.number().nullable(),
  noaCount: z.number(),
  customsClearedAt: z.string().nullable(),
  driverCollected: z.number().nullable(),
  palletScannedOrder: z.number().nullable(),
  status: z.string(),
  totalNetWeight: z.number(),
  totalNoOfBoxes: z.number(),
  noOfParcels: z.number(),
  submittedOrder: z.number(),
}) satisfies z.ZodType<ShipmentRow>;

const noaPartSchema = z.object({
  id: z.string(),
  partNumber: z.number(),
  noaDateTime: z.string(),
  pieces: z.number().nullable(),
  awbPieces: z.number().nullable(),
  hasDocument: z.boolean(),
  collectedUnits: z.number().nullable(),
  collectionStatus: z.enum(COLLECTION_STATUSES),
}) satisfies z.ZodType<NoaPart>;

export interface ShipmentsPage {
  items: ShipmentRow[];
  skip: number;
  take: number;
  total: number;
}

const pageShapeSchema = z.object({
  items: z.array(z.unknown()),
  skip: z.number(),
  take: z.number(),
  total: z.number(),
});

/**
 * Validates the page envelope, then each row independently — a single row the
 * backend sends with an otherwise malformed shape is dropped and logged
 * rather than failing `.parse()` for the whole page, which previously
 * blanked the entire list over one bad row. `status` itself is an unvalidated
 * `z.string()` (see `ShipmentRow.status`), so an out-of-spec status value no
 * longer drops the row — it renders verbatim instead.
 */
function parsePage<Row>(
  raw: unknown,
  rowSchema: z.ZodType<Row>,
): { items: Row[]; skip: number; take: number; total: number } {
  const page = pageShapeSchema.parse(raw);
  const items = page.items.flatMap((row) => {
    const result = rowSchema.safeParse(row);
    if (!result.success) {
      console.error('Skipping shipment row that failed validation', result.error.issues, row);
      return [];
    }
    return [result.data];
  });
  return { ...page, items };
}

/**
 * `status` orders by pipeline stage (`SHIPMENT_STATUSES` order) — asc runs
 * PreAlert → PalletScanned, desc the reverse — with ETA desc as the backend's
 * tiebreaker within a stage.
 */
export type ShipmentSortBy = 'eta' | 'noa' | 'customsClearedAt' | 'status';
export type SortDir = 'asc' | 'desc';

export interface ShipmentsListParams {
  skip?: number | undefined;
  take?: number | undefined;
  search?: string | undefined;
  subscriptionId?: string[] | undefined;
  etaFrom?: string | undefined;
  etaTo?: string | undefined;
  sortBy?: ShipmentSortBy | undefined;
  sortDir?: SortDir | undefined;
  /**
   * Sent as CSV, matched against each shipment's current stage exactly (an OR
   * over the listed values, not "reached this stage"). Locked to
   * `['PalletScanned']` on the Delivered tab; empty/unset means no filter.
   */
  status?: ShipmentStatus[] | undefined;
}

/**
 * Prefix shared by every cached Tollit shipments list page, whatever its
 * skip/take/search/filter/sort params. Match on this to invalidate or patch
 * all of them at once instead of guessing a single fully-specified key.
 */
export const shipmentsListQueryKey = ['shipments', 'tollit', 'list'] as const;

export function shipmentsQueryKey(params: ShipmentsListParams) {
  return [...shipmentsListQueryKey, params] as const;
}

function buildShipmentsQuery(params: ShipmentsListParams): string {
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.set('skip', String(params.skip));
  if (params.take !== undefined) query.set('take', String(params.take));
  if (params.search) query.set('search', params.search);
  if (params.subscriptionId?.length) query.set('subscriptionId', params.subscriptionId.join(','));
  if (params.etaFrom) query.set('etaFrom', params.etaFrom);
  if (params.etaTo) query.set('etaTo', params.etaTo);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDir) query.set('sortDir', params.sortDir);
  if (params.status?.length) query.set('status', params.status.join(','));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

/**
 * GET /api/v1/shipments. Tollit-only, feature-policy gated. One row per AWB
 * (`PreAlert`), not per `Package`.
 *
 * `subscriptionId` is a legitimate filter param here, not tenant scope — it
 * picks which customer's shipments to show, the same way the users list is
 * filtered by userType/status, so it is not on `FORBIDDEN_SCOPE_PARAMS`.
 */
export function shipmentsQueryOptions(api: ApiClient, params: ShipmentsListParams = {}) {
  return queryOptions({
    queryKey: shipmentsQueryKey(params),
    queryFn: async () =>
      parsePage(
        await api.request<unknown>(`${BASE}${buildShipmentsQuery(params)}`),
        shipmentRowSchema,
      ),
    staleTime: 30 * 1000,
  });
}

export function shipmentDeviationsQueryKey(params: ShipmentsListParams) {
  return [...shipmentsListQueryKey, 'deviations', params] as const;
}

/**
 * GET /api/v1/shipments/deviations. The Tollit list above narrowed server-side
 * to shipments whose submitted order count deviates from the pre-alerted parcel
 * count (`submittedOrder > 0 && submittedOrder !== noOfParcels`) — same params,
 * sorting and row shape (backend `GetShipmentDeviations`), so it reuses
 * `shipmentRowSchema`. Keyed under `shipmentsListQueryKey` so invalidating the
 * Tollit list prefix covers this page too.
 */
export function shipmentDeviationsQueryOptions(api: ApiClient, params: ShipmentsListParams = {}) {
  return queryOptions({
    queryKey: shipmentDeviationsQueryKey(params),
    queryFn: async () =>
      parsePage(
        await api.request<unknown>(`${BASE}/deviations${buildShipmentsQuery(params)}`),
        shipmentRowSchema,
      ),
    staleTime: 30 * 1000,
  });
}

const shipmentPackageSchema = z.object({
  orderNo: z.string(),
  packageStatus: z.enum(PACKAGE_STATUSES),
  totalNoOfColli: z.number(),
  totalNetWeight: z.number(),
  totalGrossWeight: z.number(),
  clearanceDate: z.string().nullable(),
  invoiceNo: z.string(),
  orderDate: z.string().nullable(),
}) satisfies z.ZodType<ShipmentPackageRow>;

const shipmentDetailSchema = shipmentRowSchema.extend({
  etd: z.string().nullable(),
  packages: z.array(shipmentPackageSchema),
  noas: z.array(noaPartSchema),
}) satisfies z.ZodType<ShipmentDetail>;

export function shipmentQueryKey(awb: string) {
  return ['shipments', 'tollit', 'detail', awb] as const;
}

/**
 * GET /api/v1/shipments/{transportDocumentNo}. Tollit-only. The pre-alert
 * plus its Packages, matched by AWB (not a DB-level FK). 404s when no
 * pre-alert matches — render the "shipment not found" state on that.
 */
export function shipmentQueryOptions(api: ApiClient, awb: string) {
  return queryOptions({
    queryKey: shipmentQueryKey(awb),
    queryFn: async () =>
      shipmentDetailSchema.parse(await api.request<unknown>(`${BASE}/${encodeURIComponent(awb)}`)),
    staleTime: 30 * 1000,
  });
}

// No `.passthrough()` — see the note on customerShipmentRowSchema below. If
// the ground handler endpoint ever regressed and leaked `subscriptionId` onto
// a row, `.parse()` would silently strip it rather than the SPA rendering it.
const groundHandlerShipmentRowSchema = z.object({
  customerName: z.string().nullable(),
  transportDocumentNo: z.string(),
  manifestNo: z.string().nullable(),
  flightNo: z.string(),
  eta: z.string(),
  noaDateTime: z.string().nullable(),
  noaQuantity: z.number().nullable(),
  noaCount: z.number(),
  customsClearedAt: z.string().nullable(),
  driverCollected: z.number().nullable(),
  palletScannedOrder: z.number().nullable(),
  status: z.string(),
  totalNetWeight: z.number(),
  totalNoOfBoxes: z.number(),
  noOfParcels: z.number(),
  submittedOrder: z.number(),
}) satisfies z.ZodType<GroundHandlerShipmentRow>;

export interface GroundHandlerShipmentsPage {
  items: GroundHandlerShipmentRow[];
  skip: number;
  take: number;
  total: number;
}

/** No `subscriptionId` — the ground handler endpoint does not accept one; a handler has no customer dropdown to populate. */
export interface GroundHandlerShipmentsListParams {
  skip?: number | undefined;
  take?: number | undefined;
  search?: string | undefined;
  etaFrom?: string | undefined;
  etaTo?: string | undefined;
  sortBy?: ShipmentSortBy | undefined;
  sortDir?: SortDir | undefined;
  /** Same CSV, exact-stage semantics as `ShipmentsListParams.status`. */
  status?: ShipmentStatus[] | undefined;
}

const GROUND_HANDLER_BASE = '/api/v1/ground-handler/shipments';

function buildGroundHandlerShipmentsQuery(params: GroundHandlerShipmentsListParams): string {
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.set('skip', String(params.skip));
  if (params.take !== undefined) query.set('take', String(params.take));
  if (params.search) query.set('search', params.search);
  if (params.etaFrom) query.set('etaFrom', params.etaFrom);
  if (params.etaTo) query.set('etaTo', params.etaTo);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDir) query.set('sortDir', params.sortDir);
  if (params.status?.length) query.set('status', params.status.join(','));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export function groundHandlerShipmentsQueryKey(params: GroundHandlerShipmentsListParams) {
  return ['shipments', 'handler', 'list', params] as const;
}

/**
 * GET /api/v1/ground-handler/shipments. Scoped server-side to the caller's
 * own ground handler via the AWB prefix map — no `subscriptionId` filter,
 * unlike the Tollit list (backend `GetGroundHandlerShipments`). `search`
 * matches the AWB, manifest number or customer name (the row already carries
 * `customerName`, unlike the customer scope's own row).
 */
export function groundHandlerShipmentsQueryOptions(
  api: ApiClient,
  params: GroundHandlerShipmentsListParams = {},
) {
  return queryOptions({
    queryKey: groundHandlerShipmentsQueryKey(params),
    queryFn: async () =>
      parsePage(
        await api.request<unknown>(
          `${GROUND_HANDLER_BASE}${buildGroundHandlerShipmentsQuery(params)}`,
        ),
        groundHandlerShipmentRowSchema,
      ),
    staleTime: 30 * 1000,
  });
}

const groundHandlerShipmentDetailSchema = groundHandlerShipmentRowSchema.extend({
  etd: z.string().nullable(),
  noas: z.array(noaPartSchema),
}) satisfies z.ZodType<GroundHandlerShipmentDetail>;

export function groundHandlerShipmentQueryKey(awb: string) {
  return ['shipments', 'handler', 'detail', awb] as const;
}

/**
 * GET /api/v1/ground-handler/shipments/{transportDocumentNo}. The narrower
 * response backend `GroundHandlerShipmentDetailResponse` sends — no
 * `packages`. Parsed with `groundHandlerShipmentDetailSchema` rather than the
 * Tollit `shipmentDetailSchema` so a backend that ever starts sending
 * `packages` here is a parse-time surprise rather than a silent render. 404s,
 * byte-identical, for an AWB that doesn't exist and one handled by another
 * ground handler.
 */
export function groundHandlerShipmentQueryOptions(api: ApiClient, awb: string) {
  return queryOptions({
    queryKey: groundHandlerShipmentQueryKey(awb),
    queryFn: async () =>
      groundHandlerShipmentDetailSchema.parse(
        await api.request<unknown>(`${GROUND_HANDLER_BASE}/${encodeURIComponent(awb)}`),
      ),
    staleTime: 30 * 1000,
  });
}

const subscriptionOptionSchema = z.object({
  subscriptionId: z.string(),
  name: z.string(),
  subCustomer: z.string().nullish(),
  billingCompanyName: z.string().nullish(),
}) satisfies z.ZodType<SubscriptionOption>;

export const subscriptionsQueryKey = ['subscriptions', 'list'] as const;

/** GET /api/v1/subscriptions. Backs the shipment list's Customer dropdown. */
export function subscriptionsQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: subscriptionsQueryKey,
    queryFn: async () =>
      z.array(subscriptionOptionSchema).parse(await api.request<unknown>('/api/v1/subscriptions')),
    staleTime: 5 * 60 * 1000,
  });
}

// Zod objects drop unknown keys by default (no `.passthrough()` anywhere in
// this file) — so if the customer endpoint ever regressed and leaked
// `customerName`/`subscriptionId` onto a row, `.parse()` silently strips it
// rather than the SPA rendering it. Do not add `.passthrough()` here.
const customerShipmentRowSchema = z.object({
  transportDocumentNo: z.string(),
  manifestNo: z.string().nullable(),
  flightNo: z.string(),
  eta: z.string(),
  noaDateTime: z.string().nullable(),
  noaQuantity: z.number().nullable(),
  noaCount: z.number(),
  customsClearedAt: z.string().nullable(),
  driverCollected: z.number().nullable(),
  palletScannedOrder: z.number().nullable(),
  status: z.string(),
  totalNetWeight: z.number(),
  totalNoOfBoxes: z.number(),
  noOfParcels: z.number(),
  submittedOrder: z.number(),
}) satisfies z.ZodType<CustomerShipmentRow>;

export interface CustomerShipmentsPage {
  items: CustomerShipmentRow[];
  skip: number;
  take: number;
  total: number;
}

/** No `subscriptionId` field — the customer contract does not accept one (sending it has no effect, it is not bound to the caller's own tenant scope). */
export interface CustomerShipmentsListParams {
  skip?: number | undefined;
  take?: number | undefined;
  search?: string | undefined;
  etaFrom?: string | undefined;
  etaTo?: string | undefined;
  sortBy?: ShipmentSortBy | undefined;
  sortDir?: SortDir | undefined;
  /** Same CSV, exact-stage semantics as `ShipmentsListParams.status`. */
  status?: ShipmentStatus[] | undefined;
}

const CUSTOMER_BASE = '/api/v1/customer/shipments';

function buildCustomerShipmentsQuery(params: CustomerShipmentsListParams): string {
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.set('skip', String(params.skip));
  if (params.take !== undefined) query.set('take', String(params.take));
  if (params.search) query.set('search', params.search);
  if (params.etaFrom) query.set('etaFrom', params.etaFrom);
  if (params.etaTo) query.set('etaTo', params.etaTo);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDir) query.set('sortDir', params.sortDir);
  if (params.status?.length) query.set('status', params.status.join(','));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export function customerShipmentsQueryKey(params: CustomerShipmentsListParams) {
  return ['shipments', 'customer', 'list', params] as const;
}

/**
 * GET /api/v1/customer/shipments. Scoped server-side to the caller's own
 * fulfilment center; `search` matches AWB or manifest no only (backend plan
 * T8), not customer name — there is no customer name on this row shape.
 */
export function customerShipmentsQueryOptions(
  api: ApiClient,
  params: CustomerShipmentsListParams = {},
) {
  return queryOptions({
    queryKey: customerShipmentsQueryKey(params),
    queryFn: async () =>
      parsePage(
        await api.request<unknown>(`${CUSTOMER_BASE}${buildCustomerShipmentsQuery(params)}`),
        customerShipmentRowSchema,
      ),
    staleTime: 30 * 1000,
  });
}

const customerShipmentDetailSchema = customerShipmentRowSchema.extend({
  etd: z.string().nullable(),
  packages: z.array(shipmentPackageSchema),
  noas: z.array(noaPartSchema),
}) satisfies z.ZodType<CustomerShipmentDetail>;

export function customerShipmentQueryKey(awb: string) {
  return ['shipments', 'customer', 'detail', awb] as const;
}

/**
 * GET /api/v1/customer/shipments/{transportDocumentNo}. 404s when the AWB
 * doesn't exist or isn't the caller's own, byte-identical either way (same
 * reasoning as the handler scope) — render the "shipment not found" state on that.
 */
export function customerShipmentQueryOptions(api: ApiClient, awb: string) {
  return queryOptions({
    queryKey: customerShipmentQueryKey(awb),
    queryFn: async () =>
      customerShipmentDetailSchema.parse(
        await api.request<unknown>(`${CUSTOMER_BASE}/${encodeURIComponent(awb)}`),
      ),
    staleTime: 30 * 1000,
  });
}
